import React, { useState, useEffect } from 'react';
import logoText from '@assets/logo.png';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { useHistory } from 'react-router-dom';
import { BlockOutlined, SupportAgentOutlined, MailOutline, LogoutOutlined, RefreshOutlined, AddBusinessOutlined } from '@mui/icons-material';
import CreateOrganizationModal from '@components/organisms/CreateOrganizationModal';
import { removeOrganizationId } from '@config/storage';
import userService from '@services/user';
import { useAppDispatch } from '@redux/hooks';
import { setUserDetails } from '@redux/reducers/user.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setOrganizationIdLocalStorage, setOrganizationIdSessionStorage } from '@config/storage';
import { UserOrganizationRoleStatus } from '@models/user.model';
import { useTranslation } from '@hooks/useTranslation';

const UserDisabled: React.FC = () => {
  // Sprint 28, story-143. Bucket 2 (post-auth) — reads activeLocale via useTranslation, never
  // re-detects. See docs/architecture.md "## Sprint 28" Decision 4.
  const tOrgLifecycle = useTranslation('orgLifecycle');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user has an active organization on mount
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        // Check if we need to clear organizationId first
        const currentOrgId = localStorage.getItem('organizationId') || sessionStorage.getItem('organizationId');

        if (currentOrgId) {
          // Clear old organizationId and reload to ensure clean state
          removeOrganizationId();
          window.location.reload();
          return;
        }

        // No organizationId in storage, make fresh request to backend
        const data = await userService.getSelfWithOrganization();

        if (data.organization && data.user) {
          // Check if user is ACTIVE in the returned organization
          const isActive = data.user.organizationRoleStatus === UserOrganizationRoleStatus.ACTIVE;

          if (isActive) {
            // User has an active organization, update state and redirect
            dispatch(setUserDetails(data.user));
            dispatch(setOrganizationData(data.organization));
            setOrganizationIdLocalStorage(data.organization.organizationId);
            setOrganizationIdSessionStorage(data.organization.organizationId);
            history.push('/dashboard');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking user status:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkUserStatus();
  }, [dispatch, history]);

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@zenhammer.com?subject=Account%20Disabled%20-%20Request%20for%20Assistance';
  };

  const handleTryAgain = () => {
    window.location.href = '/';
  };

  const handleCreateOrgSuccess = () => {
    setShowCreateOrgModal(false);
    // Clear the old organizationId so the app fetches the user's active organization
    removeOrganizationId();
    window.location.href = '/';
  };

  // Show loading while checking user status
  if (isChecking) {
    return (
      <div className="flex flex-col gap-10 items-center justify-center min-h-screen bg-indigo-950 dark:bg-neutral-900 p-4">
        <img
          src={logoText}
          alt="ZenHammer"
          className="max-w-sm w-full mb-4 md:mb-0 md:mr-6"
        />
        <div className="text-white text-lg">{tOrgLifecycle.checkingAccountStatus}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 items-center justify-center min-h-screen bg-indigo-950 dark:bg-neutral-900 p-4">
      <img
        src={logoText}
        alt="ZenHammer"
        className="max-w-sm w-full mb-4 md:mb-0 md:mr-6"
      />

      <Card className="max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
            <BlockOutlined className="w-8 h-8 text-red-600 dark:text-red-400" sx={{ fontSize: 32 }} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {tOrgLifecycle.userDisabledTitle}
          </h1>

          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
            {tOrgLifecycle.userDisabledMessage}
          </p>

          <div className="w-full bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
              {tOrgLifecycle.whatCanYouDo}
            </h2>
            <ul className="text-sm text-gray-600 dark:text-gray-400 text-left space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5">•</span>
                {tOrgLifecycle.userDisabledBulletAdmin}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5">•</span>
                {tOrgLifecycle.userDisabledBulletSupport}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5">•</span>
                {tOrgLifecycle.userDisabledBulletTryAgain}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-0.5">•</span>
                {tOrgLifecycle.userDisabledBulletNewOrg}
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="primary"
              onClick={() => setShowCreateOrgModal(true)}
              className="w-full"
              leftIcon={<AddBusinessOutlined sx={{ fontSize: 18 }} />}>
              {tOrgLifecycle.createNewOrganization}
            </Button>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                onClick={() => history.push('/logout')}
                className="flex-1"
                leftIcon={<LogoutOutlined sx={{ fontSize: 18 }} />}>
                {tOrgLifecycle.logout}
              </Button>
              <Button
                variant="secondary"
                onClick={handleTryAgain}
                className="flex-1"
                leftIcon={<RefreshOutlined sx={{ fontSize: 18 }} />}>
                {tOrgLifecycle.tryAgain}
              </Button>
              <Button
                variant="secondary"
                onClick={handleContactSupport}
                className="flex-1 whitespace-nowrap"
                leftIcon={<MailOutline sx={{ fontSize: 18 }} />}>
                {tOrgLifecycle.contactSupport}
              </Button>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 w-full">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <SupportAgentOutlined sx={{ fontSize: 18 }} />
              <span>{tOrgLifecycle.needHelp}</span>
            </div>
          </div>
        </div>
      </Card>

      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onSuccess={handleCreateOrgSuccess}
      />
    </div>
  );
};

export default UserDisabled;
